import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { fetchCheckoutSession } from "checkout-query-client/api/fetch-checkout-session";
import { BaseParams } from "checkout-query-client/hooks/types";
import { useCreditCardObserver } from "checkout-query-client/hooks/use-is-credit-card-with-surcharge";

import {
  TransactionCharge as BackendTransactionCharge,
  CookiePolicy,
  Discount,
  GetCheckoutSessionResponse,
  LineItem,
  Money,
  OrderOrigination,
  PaymentMethodSummary,
  PlainMessage,
  ProfileAddress,
  ShippingMethod,
} from "frontend-client";

import { getNumberOfDecimals } from "payment-links/src/utils/currency";

export const useCheckoutSession = ({
  apiHost,
  version,
  checkoutSessionToken,
}: BaseParams) => {
  const [isCreditCardMop, setIsCreditCardMop] = useState<boolean>(false);
  const observer = useCreditCardObserver();

  useEffect(() => {
    const unsubscribe = observer.subscribe((result) => {
      // Note === false is on purpose to cover case of `undefined`
      setIsCreditCardMop(result.data?.isCardPinlessDebit === false);
    });
    return () => unsubscribe();
  }, [observer]);

  return useQuery({
    queryKey: [
      {
        scope: "checkout-session",
        apiHost,
        version,
        checkoutSessionToken,
        isCreditCardMop,
      },
    ],
    queryFn: fetchCheckoutSession,
    select: transformCheckoutSession,
    enabled: !!checkoutSessionToken && !!apiHost,
    placeholderData: (prev) => prev,
  });
};

export const transformCheckoutSession = (
  response: GetCheckoutSessionResponse | CheckoutSession
): CheckoutSession => {
  if (isGetCheckoutSessionResponse(response)) {
    const transformedTransactionCharges =
      response?.cart?.transactionCharges?.map((charge) => {
        return {
          ...charge,
          transactionFeeAmount: new Money({
            amount: charge.transactionFeeAmount || BigInt(0),
            currencyCode: response.currencyCode,
            decimalCount: getNumberOfDecimals(
              Number(charge.transactionFeeAmount ?? 0),
              response.currencyCode ?? "USD",
              "en-US"
            ),
          }),
        };
      });

    return {
      billingAddress: response.shopperProfile?.billingAddresses.find(
        (address) =>
          address.profileAddressId === response.shopperBillingAddressId
      ),
      saveConsumerProfile:
        response.checkoutOptions?.shopperProfileOptions?.saveProfile,
      contactInformation: {
        shopperEmail: response.shopperProfile?.email,
        shopperPhoneNumber: response.shopperProfile?.phoneNumber,
      },
      shippingAddress: response.cart?.shippingAddress
        ? {
            recipientName: response.cart?.shippingAddress.recipientName,
            email: response.cart?.shippingAddress.email,
            phone: response.cart?.shippingAddress.phone,
            line1: response.cart?.shippingAddress.line1,
            line2: response.cart?.shippingAddress.line2,
            locality: response.cart?.shippingAddress.city,
            administrativeArea: response.cart?.shippingAddress.state,
            regionCode: response.cart?.shippingAddress.country,
            postalCode: response.cart?.shippingAddress.postalCode,
          }
        : undefined,
      shippingMethods: response.cart?.shippingMethods || [],
      discounts: response.cart?.discounts || [],
      cartItems: response.cart?.lineItems || [],
      orderSummary: {
        totalAmount: new Money({
          amount: response.cart?.totalTransactionAmount || BigInt(0),
          currencyCode: response.currencyCode ?? "USD",
          decimalCount: getNumberOfDecimals(
            Number(response.cart?.totalTransactionAmount ?? 0),
            response.currencyCode ?? "USD",
            "en-US"
          ),
        }),
        subtotalAmount: response.cart?.subtotalAmount,
        totalTax: response.cart?.totalTax,
        totalDiscount: new Money({
          amount: response.cart?.transactionDiscountAmount || BigInt(0),
          currencyCode: response.currencyCode ?? "USD",
          decimalCount: getNumberOfDecimals(
            Number(response.cart?.totalTransactionAmount ?? 0),
            response.currencyCode ?? "USD",
            "en-US"
          ),
        }),
        totalWithSurcharge: new Money({
          amount: response.totalTransactionAmountWithSurcharge || BigInt(0),
          currencyCode: response.currencyCode ?? "USD",
          decimalCount: getNumberOfDecimals(
            Number(response.cart?.totalTransactionAmount ?? 0),
            response.currencyCode ?? "USD",
            "en-US"
          ),
        }),
        totalShippingCost: new Money({
          amount: response.cart?.totalShippingAmount || BigInt(0),
          currencyCode: response.currencyCode ?? "USD",
          decimalCount: getNumberOfDecimals(
            Number(response.cart?.totalTransactionAmount ?? 0),
            response.currencyCode ?? "USD",
            "en-US"
          ),
        }),
        surchargeAmount: response.surchargeAmount
          ? new Money({
              amount: response.surchargeAmount,
              currencyCode: response.currencyCode ?? "USD",
              decimalCount: getNumberOfDecimals(
                Number(response.cart?.totalTransactionAmount ?? 0),
                response.currencyCode ?? "USD",
                "en-US"
              ),
            })
          : undefined,
      },
      transactionCharges: transformedTransactionCharges,
      paymentMethods: response.paymentProfile?.paymentMethods || [],
      orderOrigination: response.orderOrigination,
      reference: response.reference,
      currencyCode: response.currencyCode,
      cookiePolicy: response.checkoutOptions?.cookiePolicy,
      isPaid: response.isPaid,
    };
  }
  return response;
};

const isGetCheckoutSessionResponse = (
  res: GetCheckoutSessionResponse | CheckoutSession
): res is GetCheckoutSessionResponse => {
  return (res as GetCheckoutSessionResponse).cart !== undefined;
};

export interface CheckoutSession {
  contactInformation?: ContactInformation;
  shippingAddress?: ShippingAddress;
  shippingMethods?: Array<PlainMessage<ShippingMethod>>;
  discounts?: Array<PlainMessage<Discount>>;
  cartItems?: Array<PlainMessage<LineItem>>;
  orderSummary?: OrderSummary;
  paymentMethods?: Array<PlainMessage<PaymentMethodSummary>>;
  orderOrigination?: OrderOrigination;
  reference?: string;
  saveConsumerProfile?: boolean;
  currencyCode?: string;
  billingAddress?: PlainMessage<ProfileAddress>;
  cookiePolicy?: CookiePolicy;
  isPaid?: boolean;
  transactionCharges?: Array<TransactionCharge>;
  shipFromAddressPostalCode?: string;
  lineItemUnitOfMeasureCode?: string;
  transactionAdvice?: Array<string>;
}

export interface TransactionCharge
  extends Omit<
    Partial<PlainMessage<BackendTransactionCharge>>,
    "transactionFeeAmount"
  > {
  transactionFeeAmount: PlainMessage<Money>;
}

export interface ContactInformation {
  shopperEmail?: string;
  shopperPhoneNumber?: string[];
}

export interface ShippingAddress {
  recipientName?: string;
  email?: string;
  phone?: string;
  line1?: string;
  line2?: string;
  locality?: string;
  administrativeArea?: string;
  regionCode?: string;
  postalCode?: string;
}

export interface OrderSummary {
  totalAmount?: PlainMessage<Money>;
  totalTax?: PlainMessage<Money>;
  totalDiscount?: PlainMessage<Money>;
  totalShippingCost?: PlainMessage<Money>;
  subtotalAmount?: PlainMessage<Money>;
  totalWithSurcharge?: PlainMessage<Money>;
  surchargeAmount?: PlainMessage<Money>;
}
